import React, { useState, useEffect } from 'react';
import { useViewOnly } from '../../contexts';
import './AIConfirmationModal.css';

/**
 * AIConfirmationModal Component
 *
 * Displays pending AI action confirmations and allows users to approve or reject them.
 * Supports Hebrew and English languages.
 */
const AIConfirmationModal = ({
  confirmation,
  language = 'he',
  onApprove,
  onReject,
  onClose,
  isLoading = false
}) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const { isViewOnly } = useViewOnly();
  const viewOnlyTip = language === 'he'
    ? 'מצב צפייה בלבד פעיל — בטל את המצב כדי לבצע שינויים'
    : 'View-only mode is on — disable to make changes';

  // Calculate time remaining
  useEffect(() => {
    if (!confirmation?.expires_at) return;

    const updateTimer = () => {
      const expires = new Date(confirmation.expires_at);
      const now = new Date();
      const diff = expires - now;

      if (diff <= 0) {
        setTimeRemaining(null);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [confirmation?.expires_at]);

  if (!confirmation) return null;

  const message = language === 'he'
    ? confirmation.confirmation_message_he
    : confirmation.confirmation_message_en;

  const riskColors = {
    low: '#48BB78',
    medium: '#ECC94B',
    high: '#F56565',
    critical: '#E53E3E'
  };

  const riskLabels = {
    he: {
      low: 'נמוך',
      medium: 'בינוני',
      high: 'גבוה',
      critical: 'קריטי'
    },
    en: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Critical'
    }
  };

  const actionLabels = {
    he: {
      book_appointment: 'קביעת תור',
      cancel_appointment: 'ביטול תור',
      reschedule_appointment: 'שינוי תור',
      create_patient: 'יצירת מטופל',
      no_show_patient: 'סימון אי הגעה',
      update_patient_medical: 'עדכון מידע רפואי'
    },
    en: {
      book_appointment: 'Book Appointment',
      cancel_appointment: 'Cancel Appointment',
      reschedule_appointment: 'Reschedule Appointment',
      create_patient: 'Create Patient',
      no_show_patient: 'Mark No-Show',
      update_patient_medical: 'Update Medical Info'
    }
  };

  const handleReject = () => {
    if (showRejectInput) {
      onReject(confirmation.id, rejectReason);
    } else {
      setShowRejectInput(true);
    }
  };

  const handleApprove = () => {
    onApprove(confirmation.id);
  };

  const riskLevel = confirmation.risk_level || 'medium';

  return (
    <div className="ai-confirmation-overlay" onClick={onClose}>
      <div
        className="ai-confirmation-modal"
        onClick={(e) => e.stopPropagation()}
        dir={language === 'he' ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div
          className="ai-confirmation-header"
          style={{ borderColor: riskColors[riskLevel] }}
        >
          <div className="header-content">
            <span className="ai-icon">🤖</span>
            <h3>
              {language === 'he' ? 'אישור פעולת AI' : 'AI Action Confirmation'}
            </h3>
          </div>
          <button className="close-button" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        {/* Body */}
        <div className="ai-confirmation-body">
          {/* Risk Badge */}
          <div
            className={`risk-badge risk-${riskLevel}`}
            style={{ backgroundColor: riskColors[riskLevel] }}
          >
            {language === 'he' ? 'רמת סיכון: ' : 'Risk: '}
            {riskLabels[language][riskLevel]}
          </div>

          {/* Action Type */}
          <div className="action-info">
            <span className="action-label">
              {language === 'he' ? 'פעולה:' : 'Action:'}
            </span>
            <span className="action-type">
              {actionLabels[language][confirmation.action_type] || confirmation.action_type}
            </span>
          </div>

          {/* Confirmation Message */}
          <p className="confirmation-message">{message}</p>

          {/* Action Parameters */}
          {confirmation.action_params && Object.keys(confirmation.action_params).length > 0 && (
            <div className="action-params">
              <strong>{language === 'he' ? 'פרטים:' : 'Details:'}</strong>
              <ul>
                {Object.entries(confirmation.action_params).map(([key, value]) => (
                  <li key={key}>
                    <span className="param-key">{key}:</span>
                    <span className="param-value">{String(value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Critical Warning */}
          {riskLevel === 'critical' && (
            <div className="critical-warning">
              ⚠️ {language === 'he'
                ? 'פעולה קריטית - נדרש אישור מנהל'
                : 'Critical action - Admin approval required'}
            </div>
          )}

          {/* High Risk Warning */}
          {riskLevel === 'high' && (
            <div className="high-risk-warning">
              ⚠️ {language === 'he'
                ? 'פעולה זו לא ניתנת לביטול'
                : 'This action cannot be undone'}
            </div>
          )}

          {/* Reject Reason Input */}
          {showRejectInput && (
            <div className="reject-reason-container">
              <label>
                {language === 'he' ? 'סיבת הדחייה (אופציונלי):' : 'Rejection reason (optional):'}
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={language === 'he' ? 'הזן סיבה...' : 'Enter reason...'}
                rows={2}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ai-confirmation-footer">
          {/* Timer */}
          {timeRemaining && (
            <div className="expires-info">
              <span className="timer-icon">⏱️</span>
              <span>
                {language === 'he' ? 'תוקף:' : 'Expires in:'} {timeRemaining}
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="ai-confirmation-actions">
            <button
              className="btn-reject"
              onClick={handleReject}
              disabled={isLoading}
            >
              {isLoading ? '...' : (language === 'he' ? 'דחה' : 'Reject')}
            </button>
            <button
              className={`btn-approve ${isViewOnly ? 'disabled-view-only' : ''}`}
              onClick={handleApprove}
              disabled={isLoading || isViewOnly}
              title={isViewOnly ? viewOnlyTip : undefined}
            >
              {isLoading ? '...' : (language === 'he' ? 'אשר' : 'Approve')}
            </button>
          </div>
        </div>

        {/* Initiated By */}
        <div className="initiated-by">
          {language === 'he' ? 'מקור:' : 'Source:'} {confirmation.initiated_by}
        </div>
      </div>
    </div>
  );
};

export default AIConfirmationModal;
